import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reorder } from "framer-motion";
import { Plus, BookOpen, Layers, ListChecks, Search, Lightbulb, Home, Pencil, Archive, ArchiveRestore, Trash2, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LevelBadge, EmptyState } from "@/components/shared";
import { ADVANCEMENT_RULES } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const KIND_BASE = { style: "styles", level: "levels", skill: "skills" };
const KIND_ARCHIVE_MSG = {
  style: "This style is linked to progress or other curriculum items. Archive it to hide it from new use while keeping existing progress.",
  level: "This level is linked to progress or other curriculum items. Archive it to hide it from new use while keeping existing progress.",
  skill: "This skill has dancer assessments. Archive it to hide it from new assessments while keeping existing progress.",
};

function VideoBlock({ fileId, url, label }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let rev;
    if (fileId) {
      api.get(`/curriculum/skill-video/${fileId}`, { responseType: "blob" })
        .then((r) => { rev = URL.createObjectURL(r.data); setSrc(rev); }).catch(() => {});
    } else { setSrc(""); }
    return () => { if (rev) URL.revokeObjectURL(rev); };
  }, [fileId]);
  if (fileId && src) return <video data-testid="skill-video-file" src={src} controls className="mt-2 rounded-lg w-full max-w-sm" />;
  if (url) return <a data-testid="skill-video-link" href={url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-teal-dark underline text-sm">▶ {label} video</a>;
  return null;
}

export default function Curriculum() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [styles, setStyles] = useState([]);
  const [activeStyle, setActiveStyle] = useState(null);
  const [levels, setLevels] = useState([]);
  const [activeLevel, setActiveLevel] = useState(null);
  const [skills, setSkills] = useState([]);
  const [cats, setCats] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [sklStyleId, setSklStyleId] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("active"); // active | archived (director)

  const loadStyles = () => api.get("/curriculum/styles").then((r) => { setStyles(r.data); setActiveStyle((cur) => r.data.some((s) => s.id === cur) ? cur : (r.data.find((s) => !s.archived)?.id || r.data[0]?.id || null)); });
  const loadLevels = (styleId) => api.get(`/curriculum/levels?style_id=${styleId}`).then((r) => { setLevels(r.data); setActiveLevel((cur) => r.data.some((l) => l.id === cur) ? cur : (r.data.find((l) => !l.archived)?.id || r.data[0]?.id || null)); });
  const loadSkills = (levelId) => api.get(`/curriculum/skills?level_id=${levelId}${isDirector ? "&include_archived=true" : ""}`).then((r) => setSkills(r.data));

  const loadUnassigned = () => { if (isDirector) api.get("/curriculum/skills-unassigned").then((r) => setUnassigned(r.data)).catch(() => {}); };
  useEffect(() => { loadStyles(); api.get("/curriculum/levels").then((r) => setAllLevels(r.data)); loadUnassigned(); }, []);
  useEffect(() => { if (activeStyle) loadLevels(activeStyle); else { setLevels([]); setActiveLevel(null); } }, [activeStyle]);
  useEffect(() => { if (activeLevel) loadSkills(activeLevel); else setSkills([]); }, [activeLevel]);

  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.name]));
  const archived = view === "archived" && isDirector;
  const stylesFor = (arch) => styles.filter((s) => (isDirector ? !!s.archived === arch : !s.archived));
  const visibleStyles = isDirector ? stylesFor(archived) : styles.filter((s) => !s.archived);
  const activeStyleObj = styles.find((s) => s.id === activeStyle);
  const activeLevelObj = levels.find((l) => l.id === activeLevel);

  const levelsShown = isDirector ? levels.filter((l) => !!l.archived === archived) : levels.filter((l) => !l.archived);
  const skillsAll = isDirector ? skills.filter((s) => !!s.archived === archived) : skills;
  const skillsShown = skillsAll.filter((s) =>
    !q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.definition || "").toLowerCase().includes(q.toLowerCase()));
  const canDrag = isDirector && !archived && !q;
  const archStyleCount = styles.filter((s) => s.archived).length;

  // ---------- dialogs ----------
  const [styleDlg, setStyleDlg] = useState(false);
  const [styleForm, setStyleForm] = useState({ name: "", description: "", icon: "music" });
  const [styleEditId, setStyleEditId] = useState(null);
  const openStyleNew = () => { setStyleForm({ name: "", description: "", icon: "music" }); setStyleEditId(null); setStyleDlg(true); };
  const openStyleEdit = (s) => { setStyleForm({ ...s }); setStyleEditId(s.id); setStyleDlg(true); };
  const saveStyle = async () => {
    try {
      if (styleEditId) await api.put(`/curriculum/styles/${styleEditId}`, styleForm);
      else await api.post("/curriculum/styles", styleForm);
      toast.success("Style saved"); setStyleDlg(false); loadStyles();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const [lvlDlg, setLvlDlg] = useState(false);
  const emptyLvl = { name: "", color: "#2FB7B5", description: "", advancement_rule: "teacher_recommend" };
  const [lvlForm, setLvlForm] = useState(emptyLvl);
  const [lvlEditId, setLvlEditId] = useState(null);
  const openLvlNew = () => { setLvlForm(emptyLvl); setLvlEditId(null); setLvlDlg(true); };
  const openLvlEdit = (l) => { setLvlForm({ ...l }); setLvlEditId(l.id); setLvlDlg(true); };
  const saveLevel = async () => {
    try {
      if (lvlEditId) await api.put(`/curriculum/levels/${lvlEditId}`, lvlForm);
      else await api.post("/curriculum/levels", { ...lvlForm, style_id: activeStyle });
      toast.success("Level saved"); setLvlDlg(false); loadLevels(activeStyle);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const [sklDlg, setSklDlg] = useState(false);
  const emptySkl = { name: "", definition: "", required: true, teaching_tips: "", teacher_video: "", teaching_video_file_id: "", home_practice: "", student_video: "", home_video_file_id: "", level_id: "" };
  const [sklForm, setSklForm] = useState(emptySkl);
  const [sklEditId, setSklEditId] = useState(null);
  const openSklNew = () => { setSklForm({ ...emptySkl, level_id: activeLevel || "" }); setSklStyleId(activeStyle); setSklEditId(null); setSklDlg(true); };
  const openSklEdit = (s) => { setSklForm({ ...s }); setSklStyleId((allLevels.find((l) => l.id === s.level_id) || {}).style_id || activeStyle); setSklEditId(s.id); setSklDlg(true); };
  const uploadVideo = async (kind, input) => {
    const f = input.files[0]; if (!f || !sklEditId) return;
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", f);
    try {
      const r = await api.post(`/curriculum/skills/${sklEditId}/video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSklForm((p) => ({ ...p, [kind === "teaching" ? "teaching_video_file_id" : "home_video_file_id"]: r.data.file_id }));
      toast.success("Video uploaded"); loadSkills(activeLevel);
    } catch (e) { toast.error("Upload failed"); }
  };
  const saveSkill = async () => {
    if (!sklForm.level_id) { toast.error("Please select a level"); return; }
    try {
      if (sklEditId) await api.put(`/curriculum/skills/${sklEditId}`, sklForm);
      else await api.post("/curriculum/skills", sklForm);
      toast.success("Skill saved"); setSklDlg(false); loadSkills(activeLevel); loadUnassigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // ---------- reorder ----------
  const onReorderStyles = (newActive) => {
    setStyles([...newActive, ...styles.filter((s) => s.archived)]);
    api.post("/curriculum/styles/reorder", { ordered_ids: newActive.map((s) => s.id) }).catch(() => toast.error("Could not save order"));
  };
  const onReorderLevels = (newActive) => {
    setLevels([...newActive, ...levels.filter((l) => l.archived)]);
    api.post("/curriculum/levels/reorder", { ordered_ids: newActive.map((l) => l.id) }).catch(() => toast.error("Could not save order"));
  };
  const onReorderSkills = (newActive) => {
    setSkills([...newActive, ...skills.filter((s) => s.archived)]);
    api.post("/curriculum/skills/reorder", { ordered_ids: newActive.map((s) => s.id) }).catch(() => toast.error("Could not save order"));
  };

  // ---------- archive / delete ----------
  const reloadKind = (kind) => { loadStyles(); if (activeStyle) loadLevels(activeStyle); if (activeLevel) loadSkills(activeLevel); };
  const setArchivedFlag = async (kind, item, arch) => {
    try {
      await api.post(`/curriculum/${KIND_BASE[kind]}/${item.id}/${arch ? "archive" : "unarchive"}`);
      toast.success(arch ? "Archived" : "Restored"); reloadKind(kind);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const [del, setDel] = useState(null); // {kind, item, usage}
  const askDelete = async (kind, item) => {
    try { const r = await api.get(`/curriculum/${KIND_BASE[kind]}/${item.id}/usage`); setDel({ kind, item, usage: r.data }); }
    catch { toast.error("Could not load usage"); }
  };
  const doDelete = async () => {
    try {
      const r = await api.delete(`/curriculum/${KIND_BASE[del.kind]}/${del.item.id}`);
      toast.success(`Deleted "${r.data.deleted}"`); const kind = del.kind; setDel(null); reloadKind(kind);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const ManageBtns = ({ kind, item }) => !isDirector ? null : (
    <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <button data-testid={`edit-${kind}-${item.id}`} title="Edit" className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
        onClick={() => (kind === "style" ? openStyleEdit(item) : kind === "level" ? openLvlEdit(item) : openSklEdit(item))}><Pencil className="w-3.5 h-3.5" /></button>
      {item.archived
        ? <button data-testid={`unarchive-${kind}-${item.id}`} title="Restore" className="p-1.5 rounded-full hover:bg-teal-light text-teal-dark" onClick={() => setArchivedFlag(kind, item, false)}><ArchiveRestore className="w-3.5 h-3.5" /></button>
        : <button data-testid={`archive-${kind}-${item.id}`} title="Archive" className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500" onClick={() => setArchivedFlag(kind, item, true)}><Archive className="w-3.5 h-3.5" /></button>}
      <button data-testid={`delete-${kind}-${item.id}`} title="Delete" className="p-1.5 rounded-full hover:bg-red-50 text-red-500" onClick={() => askDelete(kind, item)}><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );

  const LevelCard = ({ l, drag }) => (
    <div data-testid={`level-item-${l.id}`} onClick={() => setActiveLevel(l.id)}
      className={`w-full text-left p-3 rounded-xl border cursor-pointer transition-colors ${activeLevel === l.id ? "border-teal bg-teal-light" : "border-gray-100 hover:bg-background"} ${l.archived ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {drag && <GripVertical className="w-4 h-4 text-gray-300 cursor-grab shrink-0" />}
          <LevelBadge name={l.name} color={l.color} small />
        </div>
        <ManageBtns kind="level" item={l} />
      </div>
      {l.archived && <span className="text-[10px] font-semibold text-gray-500">Archived</span>}
      {l.description && <p className="text-[11px] text-muted-foreground mt-1.5">{l.description}</p>}
      <p className="text-[11px] text-muted-foreground mt-1">{ADVANCEMENT_RULES[l.advancement_rule]}</p>
    </div>
  );

  const SkillCard = ({ sk, drag }) => (
    <div data-testid={`skill-row-${sk.id}`} className={`p-4 rounded-xl bg-background ${sk.archived ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-1.5">
          {drag && <GripVertical className="w-4 h-4 text-gray-300 cursor-grab shrink-0 mt-0.5" />}
          <p className="font-semibold">{sk.name}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {sk.archived && <Badge className="bg-gray-200 text-gray-600 rounded-full text-[10px]">Archived</Badge>}
          <Badge className={sk.required ? "bg-teal text-white rounded-full" : "bg-gray-100 text-gray-600 rounded-full"}>{sk.required ? "Required" : "Optional"}</Badge>
          <ManageBtns kind="skill" item={sk} />
        </div>
      </div>
      {sk.definition && <p className="text-sm mt-2">{sk.definition}</p>}
      {sk.teaching_tips && <p className="text-sm mt-2 flex gap-1.5 text-teal-dark"><Lightbulb className="w-4 h-4 shrink-0 mt-0.5" /><span><b>Teaching tip:</b> {sk.teaching_tips}</span></p>}
      <VideoBlock fileId={sk.teaching_video_file_id} url={sk.teacher_video} label="Teaching" />
      {sk.home_practice && <p className="text-sm mt-1.5 flex gap-1.5 text-muted-foreground"><Home className="w-4 h-4 shrink-0 mt-0.5" /><span><b>Home practice:</b> {sk.home_practice}</span></p>}
      <VideoBlock fileId={sk.home_video_file_id} url={sk.student_video} label="Home practice" />
    </div>
  );

  return (
    <div className="stagger-in">
      <PageHeader title="Curriculum" subtitle={isDirector ? "Independent level pathways per dance style." : "Reference skills, expectations and teaching notes for every level."} testid="curriculum-page"
        action={isDirector && !archived && <Button data-testid="add-style-button" onClick={openStyleNew} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Add Style</Button>} />

      {isDirector && (
        <div className="flex gap-2 mb-4" data-testid="curriculum-view-toggle">
          <button data-testid="view-active" onClick={() => setView("active")} className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${view === "active" ? "bg-black text-white border-black" : "bg-white"}`}>Active</button>
          <button data-testid="view-archived" onClick={() => setView("archived")} className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${view === "archived" ? "bg-black text-white border-black" : "bg-white"}`}>Archived{archStyleCount ? ` · ${archStyleCount} style${archStyleCount > 1 ? "s" : ""}` : ""}</button>
        </div>
      )}

      {isDirector && unassigned.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="unassigned-skills-banner">
          <p className="font-semibold text-amber-900 text-sm">{unassigned.length} skill(s) need a level</p>
          <p className="text-xs text-amber-800 mb-2">Assign a level so they appear in the curriculum. Nothing is deleted.</p>
          <div className="flex flex-wrap gap-2">
            {unassigned.map((s) => (
              <button key={s.id} data-testid={`unassigned-${s.id}`} onClick={() => openSklEdit(s)} className="px-3 py-1.5 rounded-full bg-white border border-amber-300 text-sm font-semibold hover:bg-amber-100">{s.name} — assign level</button>
            ))}
          </div>
        </div>
      )}

      {/* Styles */}
      {isDirector && !archived && visibleStyles.length > 1 ? (
        <Reorder.Group axis="x" values={visibleStyles} onReorder={onReorderStyles} className="flex flex-wrap gap-2 mb-3">
          {visibleStyles.map((s) => (
            <Reorder.Item key={s.id} value={s} className="cursor-grab">
              <button data-testid={`style-chip-${s.id}`} onClick={() => setActiveStyle(s.id)}
                className={`px-4 py-2 rounded-full font-semibold text-sm border transition-colors ${activeStyle === s.id ? "bg-black text-white border-black" : "bg-white"}`}>
                <GripVertical className="w-3.5 h-3.5 inline mr-1 text-gray-300" />{s.name}
              </button>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      ) : (
        <div className="flex flex-wrap gap-2 mb-3">
          {visibleStyles.length === 0 && <p className="text-sm text-muted-foreground">{archived ? "No archived styles." : "No styles yet."}</p>}
          {visibleStyles.map((s) => (
            <button key={s.id} data-testid={`style-chip-${s.id}`} onClick={() => setActiveStyle(s.id)}
              className={`px-4 py-2 rounded-full font-semibold text-sm border transition-colors ${activeStyle === s.id ? "bg-black text-white border-black" : "bg-white"} ${s.archived ? "opacity-60" : ""}`}>
              <BookOpen className="w-4 h-4 inline mr-1.5" />{s.name}
            </button>
          ))}
        </div>
      )}
      {isDirector && activeStyleObj && (isDirector ? !!activeStyleObj.archived === archived : true) && (
        <div className="flex items-center gap-1 mb-4 text-xs text-muted-foreground" data-testid="style-manage-bar">
          <span className="mr-1">Manage <b>{activeStyleObj.name}</b>:</span>
          <ManageBtns kind="style" item={activeStyleObj} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="curriculum-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search skills…" className="pl-9 rounded-full bg-white" /></div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Levels */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 h-fit">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-bold flex items-center gap-2"><Layers className="w-4 h-4 text-teal" />Levels{archived ? " (archived)" : ""}</h3>
            {isDirector && !archived && <Button size="sm" variant="outline" className="rounded-full" data-testid="add-level-button" disabled={!activeStyle} onClick={openLvlNew}><Plus className="w-3.5 h-3.5" /></Button>}
          </div>
          {canDrag && levelsShown.length > 1 && <p className="text-[11px] text-muted-foreground mb-2">Drag to reorder levels</p>}
          {levelsShown.length === 0 ? <p className="text-sm text-muted-foreground">{archived ? "No archived levels." : "No levels yet."}</p> :
            canDrag ? (
              <Reorder.Group axis="y" values={levelsShown} onReorder={onReorderLevels} className="space-y-2">
                {levelsShown.map((l) => <Reorder.Item key={l.id} value={l}><LevelCard l={l} drag /></Reorder.Item>)}
              </Reorder.Group>
            ) : <div className="space-y-2">{levelsShown.map((l) => <LevelCard key={l.id} l={l} />)}</div>}
        </div>

        {/* Skills */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-head font-bold flex items-center gap-2"><ListChecks className="w-4 h-4 text-teal" />Skills & Expectations{archived ? " (archived)" : ""}</h3>
              {activeLevelObj && <p className="text-xs text-muted-foreground mt-0.5">{skillsShown.length} skill(s){activeLevelObj.name ? ` in ${activeLevelObj.name}` : ""}{canDrag && skillsShown.length > 1 ? " · drag to reorder" : ""}</p>}
            </div>
            {isDirector && !archived && <Button size="sm" variant="outline" className="rounded-full" data-testid="add-skill-button" disabled={!activeLevel} onClick={openSklNew}><Plus className="w-3.5 h-3.5 mr-1" />Add Skill</Button>}
          </div>
          {skillsShown.length === 0 ? <EmptyState icon={ListChecks} title="No skills found" hint={archived ? "No archived skills in this level." : (q ? "Try clearing your search." : "No skills in this level yet.")} /> :
            canDrag ? (
              <Reorder.Group axis="y" values={skillsShown} onReorder={onReorderSkills} className="space-y-3">
                {skillsShown.map((sk) => <Reorder.Item key={sk.id} value={sk}><SkillCard sk={sk} drag /></Reorder.Item>)}
              </Reorder.Group>
            ) : <div className="space-y-3">{skillsShown.map((sk) => <SkillCard key={sk.id} sk={sk} />)}</div>}
        </div>
      </div>

      {isDirector && <>
        <Dialog open={styleDlg} onOpenChange={setStyleDlg}>
          <DialogContent className="bg-white"><DialogHeader><DialogTitle className="font-head">{styleEditId ? "Edit" : "Add"} Dance Style</DialogTitle></DialogHeader>
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="style-name" value={styleForm.name} onChange={(e) => setStyleForm({ ...styleForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={styleForm.description || ""} onChange={(e) => setStyleForm({ ...styleForm, description: e.target.value })} /></div>
            <DialogFooter><Button data-testid="save-style-button" onClick={saveStyle} className="rounded-full bg-teal hover:bg-teal-dark">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={lvlDlg} onOpenChange={setLvlDlg}>
          <DialogContent className="bg-white"><DialogHeader><DialogTitle className="font-head">{lvlEditId ? "Edit" : "Add"} Level</DialogTitle></DialogHeader>
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="level-name" value={lvlForm.name} onChange={(e) => setLvlForm({ ...lvlForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Color</Label><Input type="color" value={lvlForm.color} onChange={(e) => setLvlForm({ ...lvlForm, color: e.target.value })} className="h-10 p-1" /></div>
              <div className="space-y-1.5"><Label>Completion rule</Label><select className="w-full border rounded-md h-10 px-2 text-sm" value={lvlForm.advancement_rule} onChange={(e) => setLvlForm({ ...lvlForm, advancement_rule: e.target.value })}>{Object.entries(ADVANCEMENT_RULES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={lvlForm.description || ""} onChange={(e) => setLvlForm({ ...lvlForm, description: e.target.value })} /></div>
            <DialogFooter><Button data-testid="save-level-button" onClick={saveLevel} className="rounded-full bg-teal hover:bg-teal-dark">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={sklDlg} onOpenChange={setSklDlg}>
          <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-head">{sklEditId ? "Edit" : "Add"} Skill</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Dance style</Label>
                  <select data-testid="skill-style" className="w-full border rounded-md h-10 px-3" value={sklStyleId || ""} onChange={(e) => { setSklStyleId(e.target.value); setSklForm({ ...sklForm, level_id: "" }); }}>
                    <option value="">—</option>{styles.filter((s) => !s.archived).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select></div>
                <div className="space-y-1.5"><Label>Level <span className="text-teal">*</span></Label>
                  <select data-testid="skill-level" className="w-full border rounded-md h-10 px-3" value={sklForm.level_id || ""} onChange={(e) => setSklForm({ ...sklForm, level_id: e.target.value })}>
                    <option value="">Select a level…</option>{allLevels.filter((l) => l.style_id === sklStyleId && !l.archived).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select></div>
              </div>
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="skill-name" value={sklForm.name} onChange={(e) => setSklForm({ ...sklForm, name: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sklForm.required} onChange={(e) => setSklForm({ ...sklForm, required: e.target.checked })} />Required for advancement</label>
              <div className="space-y-1.5"><Label>Definition</Label><Textarea value={sklForm.definition || ""} onChange={(e) => setSklForm({ ...sklForm, definition: e.target.value })} /></div>

              <div className="rounded-xl border p-3 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-teal-dark">Teaching — directors &amp; teachers only</p>
                <div className="space-y-1.5"><Label>Teaching tips</Label><Textarea data-testid="skill-teaching-tips" value={sklForm.teaching_tips || ""} onChange={(e) => setSklForm({ ...sklForm, teaching_tips: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Teaching video link</Label><Input data-testid="skill-teaching-link" placeholder="https://…" value={sklForm.teacher_video || ""} onChange={(e) => setSklForm({ ...sklForm, teacher_video: e.target.value })} /></div>
                {sklEditId ? <div className="flex items-center gap-2"><input type="file" accept="video/*" data-testid="skill-teaching-upload" onChange={(e) => uploadVideo("teaching", e.target)} className="text-xs" />{sklForm.teaching_video_file_id && <span className="text-xs text-green-700">Uploaded ✓</span>}</div> : <p className="text-[11px] text-muted-foreground">Save the skill first to upload a teaching video file.</p>}
              </div>

              <div className="rounded-xl border p-3 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-teal-dark">Home practice — visible to dancers</p>
                <div className="space-y-1.5"><Label>Home practice</Label><Textarea data-testid="skill-home-practice" value={sklForm.home_practice || ""} onChange={(e) => setSklForm({ ...sklForm, home_practice: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Home practice video link</Label><Input data-testid="skill-home-link" placeholder="https://…" value={sklForm.student_video || ""} onChange={(e) => setSklForm({ ...sklForm, student_video: e.target.value })} /></div>
                {sklEditId ? <div className="flex items-center gap-2"><input type="file" accept="video/*" data-testid="skill-home-upload" onChange={(e) => uploadVideo("home", e.target)} className="text-xs" />{sklForm.home_video_file_id && <span className="text-xs text-green-700">Uploaded ✓</span>}</div> : <p className="text-[11px] text-muted-foreground">Save the skill first to upload a home-practice video file.</p>}
              </div>
            </div>
            <DialogFooter><Button data-testid="save-skill-button" onClick={saveSkill} className="rounded-full bg-teal hover:bg-teal-dark">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
          <DialogContent className="bg-white max-w-md" data-testid="curriculum-usage-dialog">
            <DialogHeader><DialogTitle className="font-head">{del?.usage?.can_delete ? `Delete ${del?.kind}?` : `This ${del?.kind} is in use`}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">"{del?.usage?.name}"</p>
              <div className="rounded-xl border divide-y">
                {del?.usage?.breakdown?.map((b, i) => (
                  <div key={i} className="flex justify-between px-3 py-2"><span className="text-muted-foreground">{b.label}</span><span className="font-semibold" data-testid={`usage-count-${i}`}>{b.count}</span></div>
                ))}
              </div>
              {del?.usage?.can_delete
                ? <p>No linked records. Permanent deletion cannot be undone.</p>
                : <p className="text-amber-800 bg-amber-50 rounded-lg p-2.5" data-testid="usage-archive-note">{KIND_ARCHIVE_MSG[del?.kind]}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setDel(null)}>Cancel</Button>
              {del?.usage?.can_delete
                ? <Button data-testid="confirm-delete-permanent" onClick={doDelete} className="rounded-full bg-red-600 hover:bg-red-700 text-white">Delete permanently</Button>
                : <Button data-testid="archive-instead" onClick={() => { setArchivedFlag(del.kind, del.item, true); setDel(null); }} className="rounded-full bg-teal hover:bg-teal-dark">Archive instead</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>}
    </div>
  );
}
